import {
  buttonContentContainerStyle,
  buttonLoaderContainerStyle,
  buttonRecipe,
  type ButtonVariants,
} from './button.css';
import { capsuleButtonRecipe, type CapsuleButtonVariants } from './capsule-button.css';

import { cloneElement } from 'react';

import type React from 'react';

import { cx } from '@prose-ui/legacy';
import { Slot } from '@radix-ui/react-slot';

import CircularProgress from 'Components/CircularProgress';

export type ButtonProps<AsType extends React.ElementType = React.ElementType> =
  | ({
      capsule?: false;
      children: React.ReactNode;
      as?: AsType;
      asChild?: boolean;
      isLoading?: boolean;
    } & ButtonVariants &
      React.ComponentPropsWithoutRef<AsType>)
  | ({
      capsule: true;
      children: React.ReactNode;
      as?: AsType;
      asChild?: boolean;
      isLoading?: boolean;
    } & CapsuleButtonVariants &
      React.ComponentPropsWithoutRef<AsType>);

const wrapInAsChildTernary = ({
  asChild,
  children,
  render,
}: {
  asChild: boolean;
  children: React.ReactNode;
  render: (wrappedChildren: React.ReactNode) => React.ReactNode;
}) => {
  if (!children) return null; // in case there is no children

  if (typeof children !== 'object' || !('props' in children)) return render(children); // in case the children is not an element (e.g. a string)

  return asChild
    ? cloneElement(
        children,
        undefined,
        render(
          children.props
            .children /* required because the children would take themselves as children otherwise */,
        ),
      )
    : render(children);
};

const defaultElement = 'button';
const Button = <AsType extends React.ElementType = typeof defaultElement>({
  capsule,
  asChild = false,
  as,
  variant,
  size,
  mode = 'light',
  disabled = false,
  isLoading = false,
  children,
  padded,
  sticky,
  ...props
}: ButtonProps<AsType>) => {
  const Comp = asChild ? Slot : as ?? defaultElement;

  return (
    <Comp
      className={
        !capsule
          ? buttonRecipe({
              variant: isLoading || disabled ? undefined : variant,
              size: size ?? 'default',
              mode,
              padded,
              disabled: isLoading || disabled,
            })
          : capsuleButtonRecipe({
              variant:
                isLoading || disabled
                  ? disabled
                    ? variant?.startsWith('secondary') // INFO: we have specific styles for this case (secondary & disabled)
                      ? variant
                      : undefined
                    : variant
                  : variant,
              size,
              disabled: isLoading || disabled,
              sticky,
            })
      }
      disabled={isLoading || disabled}
      {...props}
    >
      {wrapInAsChildTernary({
        asChild,
        children,
        render: (wrappedChildren) => (
          <>
            <span key="buttonContent" className={cx(buttonContentContainerStyle, { isLoading })}>
              {wrappedChildren}
            </span>
            <span key="buttonLoader" className={cx(buttonLoaderContainerStyle, { isLoading })}>
              <CircularProgress />
            </span>
          </>
        ),
      })}
    </Comp>
  );
};

export default Button;
